import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Button, List, ListItemButton, ListItemText, Typography, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { resolveStockEditId } from '../../../utils/eventSessions';
import { computeEventStatus, EVENT_STATUS } from '../../../utils/eventStatus';
import { SeatedProductFormComponent } from '../../stocks/StocksFormComponents';
import { StandardProductsEditor } from '../event-fields/ProductFormManager';
import EmptyList from '../../misc/EmptyList';
import { getEventDbId } from '../../../utils/eventIdentity';
import { getStocks } from '../../../services/stocksService';
import { formatStockDateTime } from '../../../utils/stocksUtils';
export const STOCK_FORM_PREFIX = '_stockRecord.';

const STANDARD_STOCK_GROUPS = ['deal', 'guestlist', 'general', 'vip', 'extras', 'invitation'];

/** ArrayInput Add için gruplar her zaman dizi olmalı; null/undefined Add’i kırar. */
export const normalizeEmbeddedStockRecord = (data) => {
  if (!data || typeof data !== 'object') return data;
  const type = Number(data.type) || 1;
  let stock = data.stock;

  if (type === 3) {
    stock = Array.isArray(stock) ? stock : [];
  } else {
    const base = stock && typeof stock === 'object' && !Array.isArray(stock) ? { ...stock } : {};
    STANDARD_STOCK_GROUPS.forEach((g) => {
      if (!Array.isArray(base[g])) base[g] = [];
    });
    stock = base;
  }

  return { ...data, type, stock };
};

/**
 * Stock edit tab embedded in event edit.
 * - oneoff: tek stock product form
 * - session: oturum listesi → secilen stock product form
 */
export const EventStockEditTab = ({ record, dateType }) => {
  const dataProvider = useDataProvider();
  const { setValue, getValues } = useFormContext();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sessionStocks, setSessionStocks] = useState([]);
  const [pickingSession, setPickingSession] = useState(dateType === 'session');
  /** Form ArrayInput mount edilsin diye — stock id değişince remount */
  const [formEpoch, setFormEpoch] = useState(0);

  const stockId = useWatch({ name: '_stockRecord.id' });
  const stockType = useWatch({ name: '_stockRecord.type' });
  const stockDate = useWatch({ name: '_stockRecord.date' });
  const stockTime = useWatch({ name: '_stockRecord.time' });

  const hasEventPassed = record && computeEventStatus(record).key === EVENT_STATUS.ENDED;
  const eventDbId = getEventDbId(record);
  const isSession = dateType === 'session';
  const eventRecordId = record?.id;

  /** Aynı stock için effect tekrar setValue yapmasın — useFieldArray Add’i bozar */
  const appliedStockIdRef = useRef(null);
  const dataProviderRef = useRef(dataProvider);
  dataProviderRef.current = dataProvider;

  const applyStockToForm = useCallback(
    (data) => {
      const normalized = normalizeEmbeddedStockRecord(data);
      if (!normalized?.id) return false;

      setValue('_stockRecord', normalized, { shouldDirty: false });

      // Nested path’leri ayrı yaz — useFieldArray `_stockRecord.stock.X` için sync olsun
      if (normalized.stock && !Array.isArray(normalized.stock)) {
        STANDARD_STOCK_GROUPS.forEach((g) => {
          setValue(`_stockRecord.stock.${g}`, normalized.stock[g] || [], {
            shouldDirty: false,
          });
        });
      } else if (Array.isArray(normalized.stock)) {
        setValue('_stockRecord.stock', normalized.stock, { shouldDirty: false });
      }

      appliedStockIdRef.current = normalized.id;
      setFormEpoch((n) => n + 1);
      return true;
    },
    [setValue],
  );

  const loadStockById = useCallback(
    async (nextStockId) => {
      if (!nextStockId) return false;
      const { data } = await dataProviderRef.current.getOne('stocks', { id: nextStockId });
      return applyStockToForm(data);
    },
    [applyStockToForm],
  );

  useEffect(() => {
    if (!eventRecordId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      // Bu stock zaten formda — ASLA tekrar setValue yapma (Add kırılır)
      if (appliedStockIdRef.current && getValues('_stockRecord.id') === appliedStockIdRef.current) {
        setLoading(false);
        setPickingSession(false);
        return;
      }

      setLoading(true);
      setLoadError(null);
      try {
        if (isSession) {
          const stocks = await getStocks({
            eventID: eventDbId || eventRecordId,
            limitCount: 200,
          });
          if (cancelled) return;
          setSessionStocks(Array.isArray(stocks) ? stocks : []);

          if (!stocks?.length) {
            setLoadError('no_stock');
            setPickingSession(true);
            return;
          }

          const existingId = getValues('_stockRecord.id');
          if (
            existingId &&
            appliedStockIdRef.current === existingId &&
            stocks.some((s) => s.id === existingId || s.stockID === existingId)
          ) {
            if (!cancelled) setPickingSession(false);
            return;
          }

          if (stocks.length === 1) {
            await loadStockById(stocks[0].id);
            if (!cancelled) setPickingSession(false);
            return;
          }

          // Çoklu oturum: kullanıcı seçene kadar form boş
          appliedStockIdRef.current = null;
          setValue('_stockRecord', null, { shouldDirty: false });
          if (!cancelled) setPickingSession(true);
          return;
        }

        // oneoff
        const existingId = getValues('_stockRecord.id');
        if (existingId && appliedStockIdRef.current === existingId) {
          if (!cancelled) setPickingSession(false);
          return;
        }

        const stockIdResolved = await resolveStockEditId(record);
        if (!stockIdResolved) {
          if (!cancelled) setLoadError('no_stock');
          return;
        }
        await loadStockById(stockIdResolved);
        if (!cancelled) setPickingSession(false);
      } catch (error) {
        console.error('EventStockEditTab:', error);
        if (!cancelled) setLoadError('load_failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getValues/setValue stable; reload only on event/dateType
  }, [eventRecordId, dateType, isSession, eventDbId, loadStockById]);

  const handlePickSession = async (stock) => {
    if (!stock?.id) return;
    setLoading(true);
    setLoadError(null);
    try {
      appliedStockIdRef.current = null;
      await loadStockById(stock.id);
      setPickingSession(false);
    } catch (error) {
      console.error('EventStockEditTab pick:', error);
      setLoadError('load_failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <EmptyList mode="loading" />;
  }

  const stockMeta = {
    id: stockId,
    type: Number(stockType) || 1,
    date: stockDate,
    time: stockTime,
  };
  const useSeatedForm = Number(stockType) === 3;

  let body = null;

  if (loadError === 'no_stock') {
    body = <p style={{ padding: '8px 0' }}>No stock record is linked to this event yet.</p>;
  } else if (loadError === 'load_failed') {
    body = (
      <p style={{ padding: '8px 0', color: '#b45309' }}>
        Could not load stock for this event. Try again or use the stocks list.
      </p>
    );
  } else if (isSession && pickingSession) {
    body = (
      <Box sx={{ py: 1 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Bu etkinlikte birden fazla oturum (stock) var. Ürünleri düzenlemek için bir oturum seçin.
        </Typography>
        {sessionStocks.length === 0 ? (
          <Typography color="text.secondary">Oturum bulunamadı.</Typography>
        ) : (
          <List dense sx={{ border: '1px solid #e5e7eb', borderRadius: 1 }}>
            {sessionStocks.map((s) => (
              <ListItemButton key={s.id} onClick={() => handlePickSession(s)} divider>
                <ListItemText
                  primary={formatStockDateTime(s.date, s.time) || s.id}
                  secondary={
                    <>
                      ID: {s.id}
                      {s.isActive === false ? ' · inactive' : ''}
                      {typeof s.sales === 'number' ? ` · ${s.sales} sales` : ''}
                    </>
                  }
                />
                <Chip size="small" label="Ürünleri aç" color="primary" variant="outlined" />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    );
  } else if (!stockId) {
    body = (
      <Box sx={{ py: 1 }}>
        <Typography color="text.secondary">Stock yüklenemedi.</Typography>
        {isSession && (
          <Button size="small" sx={{ mt: 1 }} onClick={() => setPickingSession(true)}>
            Oturum listesine dön
          </Button>
        )}
      </Box>
    );
  } else if (hasEventPassed) {
    body = (
      <p style={{ padding: '8px 0', color: '#6b7280' }}>
        This event has ended. Stock cannot be edited.
      </p>
    );
  } else {
    body = (
      <React.Fragment>
        {isSession && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 2,
              mb: 2,
              pb: 1,
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            {sessionStocks.length > 1 && (
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  appliedStockIdRef.current = null;
                  setPickingSession(true);
                }}
              >
                Oturumlar
              </Button>
            )}
            <Typography variant="body2" color="text.secondary">
              Oturum:{' '}
              <strong>{formatStockDateTime(stockMeta.date, stockMeta.time) || stockMeta.id}</strong>
            </Typography>
          </Box>
        )}

        {!useSeatedForm ? (
          <StandardProductsEditor
            key={`std-${stockId}-${formEpoch}`}
            basePath={`${STOCK_FORM_PREFIX}stock`}
            sourceLang={record?.sourceLanguage || 'tr'}
            isEdit
          />
        ) : (
          <SeatedProductFormComponent
            key={`seat-${stockId}-${formEpoch}`}
            stock={stockMeta}
            formPrefix={STOCK_FORM_PREFIX}
            sourceLang={record?.sourceLanguage || 'tr'}
          />
        )}
      </React.Fragment>
    );
  }

  return body;
};
